import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

export default function Privacy() {
  useSEO({
    title: "Privacy Policy - HealthMatch Pro",
    description: "Learn how HealthMatch Pro protects your personal information and privacy. Read our comprehensive privacy policy for healthcare professionals and employers.",
    keywords: "privacy policy, data protection, healthcare privacy, medical information security, HIPAA compliance"
  });

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF6F0] via-white to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-white/20 sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <Link
              to="/"
              className="flex items-center space-x-2 text-[#7A5D3B] hover:text-[#5E482E] transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Back to Home</span>
            </Link>
            <h1 className="text-lg font-semibold text-gray-800">Privacy Policy</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-10 sm:pt-12 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* TOC */}
          <aside className="lg:col-span-2">
            <div className="bg-white/90 border border-[#EEE6D9] rounded-2xl shadow-[0_6px_20px_rgba(16,24,40,.06)] p-5 sticky top-24">
              <h2 className="text-sm font-semibold text-[#7A5D3B] tracking-wide">On this page</h2>
              <nav className="mt-3 space-y-2 text-sm">
                <a href="#collect" className="block text-slate-700 hover:text-[#7A5D3B]">Information We Collect</a>
                <a href="#use" className="block text-slate-700 hover:text-[#7A5D3B]">How We Use Your Information</a>
                <a href="#sharing" className="block text-slate-700 hover:text-[#7A5D3B]">Information Sharing</a>
                <a href="#hipaa" className="block text-slate-700 hover:text-[#7A5D3B]">HIPAA Compliance</a>
                <a href="#security" className="block text-slate-700 hover:text-[#7A5D3B]">Data Security</a>
                <a href="#rights" className="block text-slate-700 hover:text-[#7A5D3B]">Your Rights</a>
                <a href="#contact" className="block text-slate-700 hover:text-[#7A5D3B]">Contact Us</a>
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="bg-white/90 border border-[#EEE6D9] rounded-2xl shadow-[0_6px_20px_rgba(16,24,40,.06)] p-8 sm:p-10">
              <h1 className="text-3xl font-semibold text-gray-900 mb-6">Privacy Policy</h1>
              <p className="text-sm text-slate-600 mb-8">We value your privacy. This policy explains what data we collect, why we collect it, and how we handle it. It applies to both candidates and employers using our platform.</p>

              <div className="space-y-10">
                <section>
                  <h2 id="collect" className="text-xl font-semibold text-[#2E261B] mb-3">Information We Collect</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    We collect information you provide directly to us, such as when you create an account, 
                    upload documents, or communicate with us. This includes personal information, professional 
                    credentials, and healthcare-related data necessary for our staffing services.
                  </p>
                </section>

                <section>
                  <h2 id="use" className="text-xl font-semibold text-[#2E261B] mb-3">How We Use Your Information</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    We use the information we collect to provide, maintain, and improve our services, 
                    including matching candidates with appropriate positions, verifying 
                    credentials, and facilitating secure communication between parties.
                  </p>
                </section>

                <section>
                  <h2 id="sharing" className="text-xl font-semibold text-[#2E261B] mb-3">Information Sharing</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    We do not sell, trade, or otherwise transfer your personal information to third parties 
                    without your consent, except as described in this policy. We may share information with 
                    healthcare facilities for employment purposes, service providers who assist us in our 
                    operations, and as required by law.
                  </p>
                </section>

                <section>
                  <h2 id="hipaa" className="text-xl font-semibold text-[#2E261B] mb-3">HIPAA Compliance</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    We are committed to protecting your health information in accordance with HIPAA 
                    regulations. We implement appropriate safeguards to protect the privacy and security 
                    of your protected health information.
                  </p>
                </section>

                <section>
                  <h2 id="security" className="text-xl font-semibold text-[#2E261B] mb-3">Data Security</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    We implement industry-standard security measures to protect your personal information 
                    from unauthorized access, use, or disclosure. This includes encryption, secure data 
                    transmission, and regular security audits.
                  </p>
                </section>

                <section>
                  <h2 id="rights" className="text-xl font-semibold text-[#2E261B] mb-3">Your Rights</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    You have the right to access, update, or delete your personal information. You may 
                    also request restrictions on how we use your information or opt out of certain 
                    communications. Contact us to exercise these rights.
                  </p>
                </section>

                <section>
                  <h2 id="contact" className="text-xl font-semibold text-[#2E261B] mb-3">Contact Us</h2>
                  <p className="text-muted-foreground leading-relaxed">
                    If you have any questions about this Privacy Policy or our data practices, 
                    please contact us at Dabessal@silallc.com or (628) 225-8487.
                  </p>
                </section>

                <section className="mt-8 pt-6 border-t border-[#EEE6D9]">
                  <p className="text-xs text-slate-500">
                    Last updated: {new Date().toLocaleDateString()} • We may update this policy from time to time and will notify you of significant changes.
                  </p>
                </section>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}